import { env, IS_DEV } from '@/lib/env'
import {
  clearShieldTokens,
  getShieldAccessExpiresAtMs,
  getShieldAccessToken,
  getShieldRefreshToken,
  hasFreshShieldAccessToken,
  storeShieldTokens,
} from './shield-token-store'
import { markLocallySignedOut } from './local-logout'
import { AuthSessionExpiredError } from './session-expired-error'

const REQUEST_TIMEOUT_MS = 30_000

type TokenBody = {
  access_token?: unknown
  expires_in?: unknown
  refresh_token?: unknown
}

type LoginInitiateResponse = {
  type?: unknown
  redirect_url?: unknown
}

// Shield rotates refresh tokens and revokes the family on reuse, so concurrent
// API calls must share one refresh request instead of racing with the same token.
let refreshInFlight: Promise<string | null> | null = null

function shieldUrl(path: string): string {
  return `${env.VITE_SHIELD_URL.replace(/\/$/, '')}${path}`
}

async function shieldFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  if (typeof init.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  return fetch(shieldUrl(path), {
    ...init,
    headers,
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  })
}

function tokenFromUrlParams(params: URLSearchParams): string | null {
  const token = params.get('access_token')
  return token?.trim() || null
}

function expiresAtFromSeconds(value: unknown): number | null {
  const seconds =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  if (!Number.isFinite(seconds) || seconds <= 0) return null
  return Date.now() + seconds * 1000
}

export function adoptShieldTokensFromUrl(
  loc: Location = window.location,
  replace: (url: string) => void = url => window.history.replaceState(null, '', url),
): boolean {
  const hashParams = new URLSearchParams(loc.hash.startsWith('#') ? loc.hash.slice(1) : loc.hash)
  const queryParams = new URLSearchParams(loc.search)
  const access = tokenFromUrlParams(hashParams) ?? tokenFromUrlParams(queryParams)
  if (!access) return false

  const refresh = hashParams.get('refresh_token') ?? queryParams.get('refresh_token')
  const expiresIn = hashParams.get('expires_in') ?? queryParams.get('expires_in')
  storeShieldTokens({
    accessToken: access,
    refreshToken: refresh?.trim() || null,
    accessExpiresAtMs: expiresAtFromSeconds(expiresIn),
  })

  for (const key of ['access_token', 'refresh_token', 'expires_in', 'token_type']) {
    hashParams.delete(key)
    queryParams.delete(key)
  }
  const search = queryParams.toString()
  const hash = hashParams.toString()
  replace(`${loc.pathname}${search ? `?${search}` : ''}${hash ? `#${hash}` : ''}`)
  return true
}

async function refreshShieldTokens(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = refreshShieldTokensOnce().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

async function refreshShieldTokensOnce(): Promise<string | null> {
  const refresh = getShieldRefreshToken()
  if (!refresh) return null

  let response: Response
  try {
    response = await shieldFetch('/v1/auth/token/refresh', {
      method: 'POST',
      body: JSON.stringify({
        refresh_token: refresh,
        client_id: env.VITE_SHIELD_CLIENT_ID,
      }),
    })
  } catch (error) {
    if (IS_DEV) console.error('[shield] token refresh failed', error)
    return null
  }
  if (!response.ok) {
    clearShieldTokens()
    return null
  }
  const body = (await response.json()) as TokenBody
  if (typeof body.access_token !== 'string' || !body.access_token) {
    clearShieldTokens()
    return null
  }
  storeShieldTokens({
    accessToken: body.access_token,
    refreshToken: typeof body.refresh_token === 'string' ? body.refresh_token : refresh,
    accessExpiresAtMs: expiresAtFromSeconds(body.expires_in),
  })
  return body.access_token
}

export function clearShieldSession(): void {
  clearShieldTokens()
  markLocallySignedOut()
}

export function expireShieldSession(): never {
  clearShieldSession()
  throw new AuthSessionExpiredError()
}

export async function refreshShieldVisionAccessToken(): Promise<string> {
  const refreshed = await refreshShieldTokens()
  if (refreshed) return refreshed
  return expireShieldSession()
}

async function startShieldLogin(): Promise<never> {
  const response = await shieldFetch('/v1/auth/login/initiate', {
    method: 'POST',
    body: JSON.stringify({
      mode: 'entra',
      client_id: env.VITE_SHIELD_CLIENT_ID,
      application: env.VITE_SHIELD_APPLICATION,
      redirect_uri: env.VITE_MSAL_REDIRECT_URI,
    }),
  })
  if (!response.ok) {
    if (IS_DEV) console.error('[shield] login initiate failed', await response.text())
    throw new Error('Shield sign-in failed')
  }
  const body = (await response.json()) as LoginInitiateResponse
  if (body.type !== 'redirect' || typeof body.redirect_url !== 'string') {
    throw new Error('Shield sign-in did not return a redirect')
  }
  window.location.assign(body.redirect_url)
  throw new Error('Redirecting to Shield sign-in')
}

export async function getShieldVisionAccessToken(): Promise<string> {
  adoptShieldTokensFromUrl()
  const current = getShieldAccessToken()
  if (current && hasFreshShieldAccessToken()) return current

  if (getShieldRefreshToken()) return refreshShieldVisionAccessToken()

  if (current && getShieldAccessExpiresAtMs() != null) return expireShieldSession()

  return startShieldLogin()
}

export async function logoutShield(): Promise<void> {
  const access = getShieldAccessToken()
  const refresh = getShieldRefreshToken()
  clearShieldTokens()
  try {
    if (access || refresh) {
      const headers = new Headers({ 'Content-Type': 'application/json' })
      if (access) headers.set('Authorization', `Bearer ${access}`)
      await shieldFetch('/v1/auth/logout', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          refresh_token: refresh ?? undefined,
          client_id: env.VITE_SHIELD_CLIENT_ID,
        }),
      })
    }
  } catch (error) {
    if (IS_DEV) console.error('[shield] logout failed', error)
  }
}
